import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MemberOnboardingModule from "components/member/MemberOnboardingModule";
import DigitalSubscriptionLearningModule from "components/member/DigitalSubscriptionLearningModule";
import DigitalCalendarTasks from "components/member/home/DigitalCalendarTasks";

import WelcomeHeader from "components/member/home/WelcomeHeader";
import Documents from "components/member/home/Documents";
import ScheduledActions from "components/member/home/ScheduledActions";
import { ActivityFeed } from "components/member/home/ActivityFeed";
import DelegationList from "components/member/home/DelegationList";
import TodoList from "components/member/home/TodoList";

import FlexBox from "components/common/FlexBox";
import UpcomingVideoCalls from "components/common/UpcomingVideoCalls";
import NoticeBanner from "components/common/NoticeBanner";
import InspirationBanner from "components/member/home/InspirationBanner";
import FeatureSuggestionsModule from "components/member/FeatureSuggestionsModule";
import GatedComponent, {
  FEATURE_FLAGS,
} from "components/helpers/GatedComponent";
import {
  HomeLayoutProps,
  coreDocumentsFromMemberDetails,
  scheduledActionsFromMemberDetails,
  DIGITAL_ONLY_FEATURES,
} from "./types";
import { FileText, CalendarClock } from "lucide-react";
export default ({
  data,
  currentUser,
  integrationCount,
  integrationsLoading,
  calendarTwoWaySyncUpsell,
  isMobile,
  isDeactivated,
  productPrompts,
  handleDismissFeatureSuggestions,
  handleDismissLightningBanner,
  handleLightningBannerClick,
  handleDismissReviewPrompt: _handleDismissReviewPrompt,
  handleDismissInspirationBanner,
  openReview: _openReview,
}: HomeLayoutProps) => {
  const navigate = useNavigate();
  const coreDocumentItems = coreDocumentsFromMemberDetails(data);
  const scheduledActionItems = scheduledActionsFromMemberDetails(data);

  return (
    <Box sx={{ maxWidth: "900px" }}>
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(calc(-50% + 25px))",
          width: "100%",
          backgroundImage: "url('../../assets/images/sparkles.png')",
          backgroundPosition: "top right",
          backgroundRepeat: "no-repeat",
          backgroundSize: "auto",
          backgroundColor: "#D9F0EB",
          zIndex: 1,
          margin: "0px -24px",
          marginTop: "-82px",
          padding: "0px 24px",
          paddingTop: "82px",
          height: "100vh",
        }}
      ></Box>
      <FlexBox
        direction="column"
        gap="16px"
        sx={{ position: "relative", zIndex: 3 }}
      >
        <WelcomeHeader user={currentUser} />
        <UpcomingVideoCalls family={data?.family!} />
        <GatedComponent
          featureName={FEATURE_FLAGS.DELEGATE_PHONE_CALL.value}
          includeAdmin={true}
          returnNull={true}
        >
          {productPrompts?.lightningTaskBannerDismissed !== "dismissed" && (
            <NoticeBanner
              title="⚡ Need to fire off a quick task?"
              subtext="Create a lightning task for a quick call or other fast action. We will send it to our queue for the first available advisor to pick up."
              buttonText="Try now!"
              dismissible={true}
              onDismiss={handleDismissLightningBanner}
              onClick={handleLightningBannerClick}
            />
          )}
        </GatedComponent>
        {productPrompts?.inspirationBannerDismissed !== "dismissed" && (
          <InspirationBanner onDismiss={handleDismissInspirationBanner} />
        )}
      </FlexBox>

      <Box
        sx={{
          boxShadow: "0px -4px 27px 0px rgba(0, 0, 0, 0.10)",
          borderRadius: "32px 32px 0px 0px",
          margin: "-24px",
          marginTop: "24px",
          padding: isMobile ? "32px 24px" : "24px",
          backgroundColor: "#FFF",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "40px" : "24px",
          position: "relative",
          zIndex: 10,
        }}
      >
        <MemberOnboardingModule
          member={data?.member!}
          family={data?.family!}
          showGoogleCalendarSyncUpsell={calendarTwoWaySyncUpsell}
        />
        {!isDeactivated && (
          <>
            <TodoList data={data} />
            {isMobile ? (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "24px" }}
              >
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FileText size={20} color="#198282" />
                      <Typography variant="h3">Documents</Typography>
                    </Box>
                    <Button
                      variant="text"
                      sx={{ height: "auto" }}
                      onClick={() => navigate("/documents")}
                    >
                      See all
                    </Button>
                  </Box>
                  <Documents documents={coreDocumentItems} hideHeader />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <CalendarClock size={20} color="#198282" />
                      <Typography variant="h3">Routines</Typography>
                    </Box>
                    <Button
                      variant="text"
                      sx={{ height: "auto", minWidth: "unset" }}
                      onClick={() => navigate("/scheduled-reminders")}
                    >
                      See all
                    </Button>
                  </Box>
                  <ScheduledActions actions={scheduledActionItems} hideHeader />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "24px",
                  alignItems: "stretch",
                }}
              >
                <FlexBox
                  variant="contained"
                  direction="column"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Documents documents={coreDocumentItems} />
                </FlexBox>
                <FlexBox
                  variant="contained"
                  direction="column"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <ScheduledActions actions={scheduledActionItems} />
                </FlexBox>
              </Box>
            )}
            <DigitalSubscriptionLearningModule />
            <DelegationList />
            <ActivityFeed family={data?.family} hideActors={true} />
            {productPrompts?.featureSuggestionsDismissed !== "dismissed" && (
              <FeatureSuggestionsModule
                features={DIGITAL_ONLY_FEATURES}
                onDismiss={handleDismissFeatureSuggestions}
              />
            )}
          </>
        )}
        {!isDeactivated && (
          <DigitalCalendarTasks
            family={data?.family!}
            integrationCount={integrationCount}
            forceLoading={integrationsLoading}
          />
        )}
      </Box>
    </Box>
  );
};
